'use client';

import type { RawProductQuery } from '@packages/gql/generated/shopping/graphql';

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { HorizontalScroll } from '../../HorizontalScroll/HorizontalScroll.client';
import { ImageGalleryCallToActionButton } from '../../ImageGalleryCallToActionButton/ImageGalleryCallToActionButton.client';
import { ImageGalleryColorDialogItem } from './ImageGalleryColorDialogItem/ImageGalleryColorDialogItem.client';
import { useImageGalleryColorDialogTracking } from './useImageGalleryColorDialogTracking/useImageGalleryColorDialogTracking';

export interface ImageGalleryColorDialogProps {
  dimensions: NonNullable<RawProductQuery['product']>['dimensions'];
}

export const ImageGalleryColorDialog = ({ dimensions }: ImageGalleryColorDialogProps) => {
  const [openColorDialog, setOpenColorDialog] = useState(false);
  const { formatMessage } = useIntl();
  const { trackColorPickerClick, trackDialogClose } = useImageGalleryColorDialogTracking();

  const handleColorPickerClick = () => {
    trackColorPickerClick();
    setOpenColorDialog(true);
  };

  const colorDimension = dimensions.find(({ axisType }) => axisType === 'COLOR');

  if (!colorDimension) {
    return null;
  }

  const modalCloseHandler = () => {
    trackDialogClose();
    setOpenColorDialog(false);
  };

  const activeItemIndex = colorDimension.values.findIndex(({ isSelected }) => isSelected);

  return (
    <>
      <ImageGalleryCallToActionButton
        onClick={handleColorPickerClick}
        buttonAriaLabel={formatMessage({
          id: 'imagegallery.colorPickerDialog.buttonName',
          defaultMessage: 'weitere Farben anzeigen',
        })}
      >
        <span className="icon-emp-color-picker size-4" />
      </ImageGalleryCallToActionButton>
      {openColorDialog && (
        <Dialog isOpen onClose={modalCloseHandler} variant="bottomSheet">
          <DialogHeader onClose={modalCloseHandler}>
            {formatMessage({
              id: 'imagegallery.colorPickerDialog.selectColor',
              defaultMessage: 'Farbe wählen',
            })}
          </DialogHeader>
          <DialogContent className="max-w-[100vw] px-0">
            <HorizontalScroll
              className="items-stretch"
              gap="0.75rem"
              offset="1rem"
              scrollToItemIndex={activeItemIndex}
            >
              {colorDimension.values.map((value, index) => (
                <ImageGalleryColorDialogItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  value={value}
                  onSelectCallback={modalCloseHandler}
                />
              ))}
            </HorizontalScroll>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
